import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import useDeepCompareEffect from "use-deep-compare-effect";
import { SearchOutlined } from "@ant-design/icons";
import {
  message,
  Button,
  Table,
  TableProps,
  Confirm,
  ToolbarStateProps,
} from "@gw/web-basic-components";
import { cloneDeep } from "lodash";
import classNames from "classnames";
import { getDeepProp } from "@gw/gw-utils";
import {
  GWSearch,
  openFormModal,
  RelationModal,
  LeadingInModal,
  ColSearch,
  Time,
} from "./components";
import {
  OPERATION_BASIC_KEY,
  ROW_ICON_MAP,
  ROW_ICON_TITLE,
  TEMPLATE_DOWNLOAD_PRE,
} from "./config";
import { useBusinessConfig } from "..";
import type {
  ITableProps,
  SortParams,
  TableColumnProps,
  IRelationModalProps,
  ILeadingModalProps,
  FormModalConfig,
  PaginationProps,
  GetList,
  HandleTableChange,
  ColSearchParam,
} from "./dto";
import styles from "./index.module.less";
import OperationMenu from "./components/operationMenu";

/**
 * 业务表格组件
 * @deprecated 1.x版本组件，不再维护，请尽快迁移至本地项目
 */
const GWTable = <T extends Object = any>(props: ITableProps<T>) => {
  const { apis, useRequest } = useBusinessConfig();

  // 标记当前选中哪行数据
  const [, /* selectedRecord */ setSelectedRecord] =
    useState<Record<string, any>>(null);
  // 表格展示列
  const [columns, setColumns] = useState<TableColumnProps<any>[]>(
    props.columns || []
  );
  // 从表格中勾选出的行
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const [selectedRows, setSelectedRows] = useState<T[]>([]);

  // confirm 确认
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [confirmObj, setConfirmObj] = useState({
    inBatch: null,
    deleteIds: null,
    record: null,
  });

  // 表格分页
  const [pagination, setPagination] = useState<PaginationProps>({
    current: 1,
    pageSize: 8,
    total: 0,
    pageSizeOptions: ["8", "15", "20", "50", "100"],
    showSizeChanger: true,
  });
  // 表格排序
  const [sortParam, setSortParam] = useState<SortParams[]>(null);

  // 表格搜索
  const [searchParam, setSearchParam] = useState<{
    key: string;
    value: string;
  }>({ key: "", value: "" });
  const [toolbarState, setToolbarState] = useState<ToolbarStateProps>({});
  // 表格筛选
  const [filterParam, setFilterParam] = useState<Record<string, any>>({});
  const [filterVisible, setFilterVisible] = useState("");
  // 表格额外参数（例如物模板id号）
  const [extraParam, setExtraParam] = useState<Record<string, any>>(
    props.extraParam
  );

  // 表格数据初始化
  const [listData, setListData] = useState<Record<string, any>[]>([]);

  // 行操作按钮配置
  const [, setRawOptConfig] = useState(
    props.rawOptConfig || {
      delete: true,
      edit: true,
    }
  );
  // 关联框配置
  const [relationModalConfig, setRelationModalConfig] =
    useState<IRelationModalProps>(null);

  /**
   * 批量上传配置
   */
  const [leadingInConfig, setLeadingInConfig] = useState<ILeadingModalProps>(
    props.leadingInConfig
  );
  //表格上方操作区按钮loading
  const [optLoading, setOptLoading] = useState(false);
  //行操作区loading
  // const [rowLoading, setRowLoading] = useState(false);
  const rowLoadingRef = useRef(false);
  // const [operationLoading, setOperationLoading] = useState(false);
  const formModal = useRef<any>(null);

  // 在表格中勾选行的回调
  const onSelectChange = (rowKeys, rows) => {
    setSelectedRowKeys(rowKeys);
    setSelectedRows(rows);
  };

  // 表格的行选择配置
  const rowSelection: TableProps<T>["rowSelection"] = {
    selectedRowKeys,
    columnWidth: 60,
    onChange: onSelectChange,
  };

  // 获取列表数据接口
  const [listDataState, fetchListData] = useRequest(
    apis[props.tableName].index,
    {
      parallel: false,
    }
  );
  // 获取详情数据接口
  const [, fetchDetailData] = useRequest(apis[props.tableName].show, {
    parallel: false,
  });
  // 请求删除接口
  const [, requestDelete] = useRequest(apis[props.tableName].delete, {
    parallel: false,
  });
  // 请求新增接口
  const [, addItem] = useRequest(apis[props.tableName].create, {
    parallel: false,
  });
  // 请求更新接口
  const [, updateItem] = useRequest(apis[props.tableName].update, {
    parallel: false,
  });
  // 批量导出
  const [, fetchExportContent] = useRequest(
    apis[props.tableName].exportContent
  );

  // 执行获取列表数据
  const getList: GetList = async (
    _pagination,
    _sorter?,
    _searchParam?,
    _filters?,
    _extraParam?
  ) => {
    const params: any = {
      ...{ current: _pagination.current, size: _pagination.pageSize },
      ..._sorter,
      ...(_searchParam?.key && _searchParam?.value && _searchParam),
      ..._extraParam,
    };
    if (props.isFilterTable) {
      params.queryConditions = [];
      if (_searchParam?.key && _searchParam?.value) {
        params.queryConditions.push({
          column: _searchParam.key,
          matchType: "Like",
          matchValue: _searchParam.value,
        });
      }
      if (props.tabsConfig) {
        params.queryConditions.push({
          column: props.tabsConfig?.key,
          matchValue: props.tabsConfig?.value,
          matchType: props.tabsConfig?.matchType,
        });
      }
      if (_filters && Object.values(_filters).length) {
        const queryConditions = Object.values(_filters);
        params.queryConditions.push(...queryConditions);
      }
    }

    if (_sorter?.length) {
      params["orders"] = _sorter;
    }
    if (props.notPage) {
      params.current = 1;
      params.size = -1;
    }
    try {
      const res: IResDataList<T> = await fetchListData(params);
      let list = res.dataList;
      if (props?.dataTransform?.beforeList) {
        list = await props.dataTransform.beforeList(res);
      }

      setListData(list);
      if (!props.notPage) {
        setPagination((state) => {
          return {
            ...state,
            pageSize: Number(res.size ?? 8),
            total: Number(res.total),
          };
        });
      }
      onSelectChange([], []);
    } catch (error) {
      console.error(error);
      setListData([]);
    }
  };

  // 执行删除
  const deleteItems = (rowKeys: string[], record: object = {}) => {
    const body = {
      idList: rowKeys,
    };
    const params = {
      ...extraParam,
      ...body,
    };
    /**
     * 判断是否要在path上添加额外参数
     * array 直接添加，function 判断结果类型
     */
    let deleteParams = props.rawOptConfig?.delete;
    if (typeof deleteParams === "function") {
      deleteParams = deleteParams(record);
    }
    if (Array.isArray(deleteParams) && deleteParams?.length) {
      deleteParams.forEach((param) => {
        params[param] = record[param];
      });
    }
    requestDelete(params)
      .then(() => {
        message.success("删除成功");
        getList(pagination, sortParam, searchParam, filterParam, extraParam);
      })
      .catch((error) => {
        console.error(error);
      });
  };

  // 点击行操作按钮 删除(单个) 的回调
  const doDelete = (
    deleteIds: string[],
    inBatch: boolean,
    record?: Record<string, any>
  ) => {
    setConfirmVisible(true);
    setConfirmObj({
      inBatch,
      deleteIds,
      record,
    });
  };

  const handleAddItem = (formData, type, callback?: Function) => {
    // console.log("新增入参", formData);
    callback?.(true);
    addItem({ ...extraParam, ...formData })
      .then(() => {
        // 关闭modal
        if (type === "copyContinue") {
          // 复制新增
          callback?.(false);
        } else if (type === "saveContinue") {
          // 保存并继续新增
          formModal.current?.reset();
          callback?.(false);
        } else {
          // 保存
          formModal.current?.destroy([{ triggerCancel: true }]);
          callback?.(false);
        }
        message.success(props.tipsConfig?.add || "新增成功");
        getList(pagination, sortParam, searchParam, filterParam, extraParam);
        rowLoadingRef.current = false;
      })
      .catch(() => callback?.(false));
  };

  const handleUpdateItem = (record, formData, callback?: Function) => {
    // console.log("编辑入参", { ...extraParam, id: record.id, ...formData });

    callback?.(true);
    updateItem({ ...extraParam, id: record.id, ...formData })
      .then(() => {
        callback?.(false);
        // 关闭modal
        formModal.current?.destroy([{ triggerCancel: true }]);
        message.success(props.tipsConfig?.update || "编辑成功");
        if (props.formModalConfig?.afterUpdate) {
          props.formModalConfig.afterUpdate(formData, record).then(() => {
            getList(
              pagination,
              sortParam,
              searchParam,
              filterParam,
              extraParam
            );
          });
        } else {
          // console.log("getList before", pagination);
          getList(pagination, sortParam, searchParam, filterParam, extraParam);
        }
      })
      .catch(() => callback?.(false));
  };

  // 打开编辑弹框
  const openEditModal = async (
    record,
    isAdd,
    exOptions?: Record<string, any>
  ) => {
    /**
     * table配置文件名
     */
    if (!props.tableName) {
      console.error("can not find the tableName");
      return;
    }
    let formGroupData;
    if (props?.formModalConfig?.formGroupData) {
      formGroupData = cloneDeep(props.formModalConfig.formGroupData);
    } else {
      // console.error("can not find formGroupData config");
      return;
    }

    let extendsButton = props.formModalConfig?.extendsButton
      ? [...props.formModalConfig?.extendsButton]
      : null;
    /**
     * 表单数据特殊处理
     */
    if (props.formModalConfig?.handleFormGroupData) {
      formGroupData = await props.formModalConfig.handleFormGroupData(
        formGroupData,
        record,
        isAdd
      );
    }
    /**
     * 是否查看表单，查看行信息
     */
    if (exOptions?.disabled) {
      formGroupData = Object.keys(formGroupData).reduce((pre, cur) => {
        return {
          ...pre,
          [cur]: formGroupData[cur].map((m) => {
            return {
              ...m,
              disabled: true,
            };
          }),
        };
      }, []);
    }
    /**
     * 底部按钮特殊处理(主要针对新增和编辑底部按钮不同处理)
     */
    if (
      props.formModalConfig?.handleButton &&
      props.formModalConfig?.extendsButton
    ) {
      extendsButton = props.formModalConfig.handleButton(extendsButton, isAdd);
    }
    /**
     * 表单配置
     */
    const formConfig: FormModalConfig = {
      title: isAdd
        ? props.formModalConfig?.addTitle || "新增"
        : props.formModalConfig?.editTitle || "编辑",
      onOk: (event, type, callback) => {
        // console.log("event", { ...event });
        if (exOptions?.disabled) {
          formModal.current?.destroy([{ triggerCancel: true }]);
          return;
        }
        let formData = event;
        if (props.dataTransform?.afterEdit) {
          formData = props.dataTransform.afterEdit(formData, record);
        }
        const body = {};
        Object.values(formData).forEach((m) => {
          Object.assign(body, m);
        });
        if (isAdd) {
          handleAddItem(body, type, callback);
        } else {
          handleUpdateItem(record, body, callback);
        }
      },
      onCancel: (event) => {
        // console.log("[GWTable] Cancel", event);
      },
      configProvider: { apis, useRequest },
      okButtonProps: { htmlType: "submit" },
      formGroupData,
      formGroupName: props.formModalConfig.formGroupName,
      record: record,
      extendsButton: extendsButton,
      onValuesChange: props?.formModalConfig?.onValuesChange,
    };
    /**
     * 表单项赋初始值
     */
    if (!isAdd && record) {
      Object.keys(formGroupData)?.forEach((groupKey) => {
        formGroupData[groupKey].forEach((formItem) => {
          formItem.value = formItem.valueField
            ? getDeepProp(record, formItem.valueField)
            : record[formItem.name] ?? formItem.value;
        });
      });
    }

    formModal.current = openFormModal(formConfig);

    rowLoadingRef.current = false;
  };

  // 点击行操作按钮 编辑 的回调
  const doEdit = (record, isAdd: boolean, options?: Record<string, any>) => {
    // 详情数据直接在list接口返回里
    if (props.detailInList) {
      setSelectedRecord(record);
      let data = record;
      if (props?.dataTransform?.beforeEdit) {
        data = props?.dataTransform?.beforeEdit(record);
      }
      openEditModal(data, isAdd, options);
    } else {
      const params = {
        id: record.id,
        ...extraParam,
      };
      /**
       * 判断是否要添加额外参数
       * array 直接添加，function 判断结果类型
       */
      let editParams = props.rawOptConfig?.edit || props.rawOptConfig?.show;
      if (typeof editParams === "function") {
        editParams = editParams(record);
      }

      if (Array.isArray(editParams) && editParams?.length) {
        editParams.forEach((param) => {
          params[param] = record[param];
        });
      }
      // 需要调后台接口获取详情
      fetchDetailData({ ...params }).then((res) => {
        // console.log("单个详情-----", res);

        let data = res;
        if (props?.dataTransform?.beforeEdit) {
          data = props?.dataTransform?.beforeEdit(res);
        }
        setSelectedRecord(record);
        openEditModal(data, isAdd, options);
      });
    }
  };

  // 点击行操作按钮 关联 的回调
  const doBind = (record) => {
    const params = { id: record.id };
    /**
     * 判断是否要在path上添加额外参数
     * array 直接添加，function 判断结果类型
     */
    let bindParams = props.rawOptConfig?.bind;
    if (typeof bindParams === "function") {
      bindParams = bindParams(record);
    }
    if (Array.isArray(bindParams) && bindParams?.length) {
      bindParams.forEach((param) => {
        params[param] = record[param];
      });
    }
    // console.log("doBind", record);
    setSelectedRecord(record);
    const relationConfig = props.buildRelationModalConfig(params);
    setRelationModalConfig(relationConfig);
    rowLoadingRef.current = false;
  };

  // 行操作按钮事件
  const handleIconEvent = (event: any, record, op) => {
    if (rowLoadingRef.current) return;
    rowLoadingRef.current = true;
    switch (op) {
      case "edit":
        doEdit(record, false);
        break;
      case "show":
        doEdit(record, false, { disabled: true });
        break;
      case "bind":
        doBind(record);
        break;
      case "delete":
        // console.log(record);
        doDelete([record.id], false, record);
        break;
      default:
        rowLoadingRef.current = false;
        break;
    }
  };
  /**
   * 处理表格的改动（分页，过滤，排序的配置改动）
   */
  const handleTableChange: HandleTableChange = (_pagination) => {
    setPagination((state) => ({ ...state, ..._pagination }));
  };
  // 新版table排序兼容处理
  useEffect(() => {
    const state = toolbarState?.sortableColState || [];
    const sortParams = state
      .map((item) => {
        if (item.order) {
          return {
            column: item.dataIndex,
            asc: item.order === "ascend",
          };
        } else {
          return null;
        }
      })
      .filter((item) => item);
    setSortParam(sortParams);
    setPagination((s) => ({ ...s, ...pagination }));
  }, [toolbarState?.sortableColState]);

  /**
   * 点击表格上方按钮 新增 的回调
   * @param event
   */
  const handleAdd = (event: any) => {
    // console.log("handleAdd", event?.target);
    setOptLoading(true);
    openEditModal(null, true);
  };
  /**
   * 点击表格上方按钮 删除(批量) 的回调
   * @param event
   */
  const handleDelete = (event: any) => {
    // console.log("handleDelete", selectedRowKeys, event?.target);
    if (selectedRowKeys.length === 0) {
      message.warn(props.tipsConfig?.unSelect || "请先选择需要删除的内容！");
      return;
    }
    setOptLoading(true);
    doDelete(selectedRowKeys, true);
  };
  /**
   * 点击表格上方按钮批量导入
   * @param event
   */
  const handleLeadingIn = (event: any) => {
    // console.log("handleLeadingIn", event?.target);
    setOptLoading(true);
    setLeadingInConfig((config) => ({
      ...config,
      visible: true,
    }));
  };
  /**
   * 点击表格上方按钮批量导出
   * @param event
   */
  const handleLeadingOut = (event: any) => {
    if (selectedRowKeys.length === 0) {
      message.warn("请先选择需要导出的内容");
      return;
    }
    // console.log("handleLeadingOut", event?.target);
    setOptLoading(true);
    fetchExportContent({ idList: selectedRowKeys })
      .then((filename) => {
        window.location.href =
          (props.leadingOutConfig?.templateDownloadPre ||
            TEMPLATE_DOWNLOAD_PRE) + filename;
      })
      .catch((msg) => message.error(msg));
  };
  /**
   * 点击表格上方按钮 关联 的回调
   */
  const handleBind = () => {
    if (!props.buildRelationModalConfig) {
      console.error("lack of relation modal config");
      return;
    }
    // console.log("handleBind", extraParam);
    setOptLoading(true);
    const newConfig = props.buildRelationModalConfig(extraParam);
    setRelationModalConfig(newConfig);
  };

  // 点击关联弹框 关闭 的回调
  const handleRelationOk = () => {
    // console.log("handleRelationOk");
    const newConfig = { ...relationModalConfig, isModalVisible: false };
    setRelationModalConfig({ ...newConfig });
    if (newConfig.refresh) {
      getList(pagination, sortParam, searchParam, filterParam, extraParam);
    }
  };
  // 点击批量导入 确认 的回调
  const handleLeadingInOk = () => {
    // 关闭弹窗
    setLeadingInConfig((origin) => ({
      ...origin,
      visible: false,
    }));
    // 刷新页面
    getList(pagination, sortParam, searchParam, filterParam, extraParam);
  };
  // 点击批量导入 取消 的回调
  const handleLeadingInCancel = () => {
    // 关闭弹窗
    setLeadingInConfig((origin) => ({
      ...origin,
      visible: false,
    }));
  };

  // 映射内置全局操作
  const optBarPropsMap = {
    add: { type: "primary", children: "新增", onClick: handleAdd },
    delete: {
      children: "批量删除",
      type: "primary",
      ghost: true,
      danger: true,
      onClick: handleDelete,
    },
    leadingIn: {
      type: "primary",
      ghost: true,
      children: "批量导入",
      onClick: handleLeadingIn,
    },
    leadingOut: {
      type: "primary",
      ghost: true,
      children: "批量导出",
      onClick: handleLeadingOut,
    },
    bind: { type: "primary", children: "关联", onClick: handleBind },
  };

  /**
   * 搜索框输入完成后点击 搜索 或 enter 回调
   * @param type
   * @param inputValue
   */
  const search = (type: string, inputValue: string) => {
    setPagination({ ...pagination, current: 1 });
    setSearchParam({ key: type, value: inputValue.trim() });
  };
  /**
   * 自定义筛选下拉菜单显隐
   */
  const handleFilterVisible = (col: string = "") => {
    setFilterVisible(col);
  };

  /**
   * 插件中刷新列表
   */
  const pluginRefreshTable = useCallback(
    (refresh: boolean | "init") => {
      if (refresh && refresh === "init" && pagination.current !== 1) {
        setPagination((page) => ({
          ...page,
          current: 1,
        }));
      } else if (refresh) {
        getList(pagination, sortParam, searchParam, filterParam, extraParam);
      }
    },
    [
      pagination.current,
      pagination.pageSize,
      sortParam,
      searchParam,
      filterParam,
      extraParam,
    ]
  );

  useEffect(() => {
    if (props.onGwTableChange)
      props.onGwTableChange({
        selectedRowKeys,
        selectedRows,
        filterParams: filterParam,
      });
  }, [selectedRowKeys, filterParam]);

  useEffect(() => {
    if (!optLoading) return;
    const timer = setTimeout(() => {
      setOptLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, [optLoading]);

  useEffect(() => {
    if (props.extraParam) {
      setExtraParam(props.extraParam);
    }
  }, [props.extraParam]);

  // 挂载和当页数变化，排序信息变化时执行
  useDeepCompareEffect(() => {
    getList(pagination, sortParam, searchParam, filterParam, extraParam);
  }, [
    props.flag,
    props.tableName,
    pagination.current,
    pagination.pageSize,
    sortParam,
    searchParam.value,
    filterParam,
    extraParam,
  ]);
  // format ColSearch Params
  const formatSearchParams = useCallback(
    (param: ColSearchParam | ColSearchParam[]) => {
      const newFilter = cloneDeep(filterParam);
      const p = Array.isArray(param) ? param : [param];

      p.forEach((v) => {
        // 处理 v
        if (v?.column) {
          if (v?.matchValue === "") {
            delete newFilter[v.column];
          } else {
            newFilter[v.column] = v;
          }
        } else if (typeof v === "string") {
          delete newFilter[v];
        }
      });

      setFilterParam(newFilter);
      setPagination((conf) => ({ ...conf, current: 1 }));
    },
    [filterParam]
  );
  /**
   * 操作列icon配置
   */
  useEffect(() => {
    setRawOptConfig(props.rawOptConfig);
    const cols = [];
    props.columns?.forEach((col) => {
      const newCol: TableColumnProps<any> = cloneDeep(col);
      if (col.filterType && !col.searchable && !col.filterDropdown) {
        newCol.filterDropdown = (
          <ColSearch
            visible={filterVisible}
            tableName={props.tableName}
            extraParam={props.extraParam}
            requestConfig={col.requestConfig}
            baseParams={filterParam}
            onChange={(param: ColSearchParam) => {
              formatSearchParams(param);
            }}
            options={col.options}
            matchType={col.filterType}
            showOptions={col.filterSearch}
            colIndex={col.dataIndex}
            colName={typeof col.title === "string" ? col.title : col.title.name}
            onClose={handleFilterVisible}
          />
        );

        newCol.filterIcon = () => (
          <SearchOutlined
            id={`${col.dataIndex}_icon`}
            onClick={() => handleFilterVisible(col.dataIndex)}
            title={
              filterParam && filterParam[col.dataIndex]
                ? "已筛选"
                : "单击进行筛选"
            }
            style={{
              color:
                filterParam && filterParam[col.dataIndex]
                  ? "#1890ff"
                  : undefined,
            }}
            className={styles.colsearch}
          />
        );
        newCol.filterDropdownVisible = filterVisible === col.dataIndex;
      }
      if (col.timeformat) {
        newCol.render = (time) => {
          return <Time time={time} />;
        };
      }
      cols.push(newCol);
    });

    if (props.rawOptConfig) {
      const renderOpts = (text, record: T) => {
        const originButtons = Object.getOwnPropertyNames(
          props.rawOptConfig
        ).filter((key) => {
          const judge = props.rawOptConfig[key];
          if (!judge) return false;
          if (typeof judge === "boolean") return judge;
          if (typeof judge === "function") return judge(record);
          return true;
        });

        const basicOperations = [];
        const expandOperations = [];

        const assignOperations = (key, value) => {
          if (OPERATION_BASIC_KEY.includes(key)) {
            basicOperations.push(value);
          } else {
            expandOperations.push(value);
          }
        };
        for (const op of originButtons) {
          if (props.rawIconSvgConfig?.[op]) {
            assignOperations(op, {
              component: props.rawIconSvgConfig[op],
              key: op,
              onClick: (event: any) => handleIconEvent(event, record, op),
            });
          } else if (props?.plugins && props.plugins[op]) {
            const plugin = props.plugins[op];
            if (plugin.component) {
              assignOperations(op, {
                key: op,
                component: (
                  <plugin.component
                    key={op}
                    record={record}
                    type="row"
                    refresh={pluginRefreshTable}
                  />
                ),
              });
            }
          } else if (ROW_ICON_MAP[op]) {
            assignOperations(op, {
              component: ROW_ICON_MAP[op]({
                title: props.rawIconTitleConfig?.[op] || ROW_ICON_TITLE[op],
              }),
              key: op,
              title: props.rawIconTitleConfig?.[op] || ROW_ICON_TITLE[op],
              onClick: (event: any) => handleIconEvent(event, record, op),
            });
          }
        }
        return (
          <OperationMenu
            basicOperations={basicOperations}
            expandOperations={expandOperations}
            titleConfig={props.rawIconTitleConfig}
          />
        );
      };

      const options = Object.getOwnPropertyNames(props.rawOptConfig).filter(
        (key) => {
          const judge = props.rawOptConfig[key];
          if (!judge) return false;
          if (typeof judge === "boolean") return judge;
          return true;
        }
      );

      const optCol = {
        title: "操作",
        key: "action",
        render: renderOpts,
        className: styles.optIcon,
        fixed: "right",
        ellipsis: false,
        /** length * 20 + (length - 1) * 20 + 2 * padding  */
        width: options.length * 20 * 2 + 20,
      };
      cols.push(optCol);
    }
    setColumns(cols);
  }, [
    props.columns,
    props.rawOptConfig,
    props.plugins,
    pagination.current,
    pagination.pageSize,
    sortParam,
    searchParam,
    filterParam,
    extraParam,
    listData,
    filterVisible,
  ]);

  const hasToolBar = props.searchConfig || props.operationBarConfig;
  const renderSearchPopoverProps = useMemo(() => {
    return props.columns
      .map((col) => {
        const visible = filterParam && filterParam[col.dataIndex];
        if (col.filterType && col.searchable && !col.renderFilterDropdown) {
          return {
            dataIndex: col.dataIndex,
            showSearchIcon: visible,
            popoverProps: {
              className: styles.colSearch,
              content: (
                <ColSearch
                  visible={filterVisible}
                  tableName={props.tableName}
                  extraParam={props.extraParam}
                  requestConfig={col.requestConfig}
                  baseParams={filterParam}
                  onChange={(param: ColSearchParam) => {
                    formatSearchParams(param);
                  }}
                  options={col.options}
                  matchType={col.filterType}
                  showOptions={col.filterSearch}
                  colIndex={col.dataIndex}
                  colName={
                    typeof col.title === "string" ? col.title : col.title.name
                  }
                  onClose={() =>
                    setToolbarState((prev) => ({
                      ...prev,
                      searchVisibleCol: "",
                    }))
                  }
                />
              ),
            },
          };
        } else if (col.searchable && col.renderFilterDropdown) {
          const renderElement = col?.renderFilterDropdown();
          return {
            dataIndex: col.dataIndex,
            showSearchIcon: visible,
            popoverProps: {
              className: col?.filterDropdownClassName,
              content: React.isValidElement(renderElement)
                ? React.cloneElement(renderElement, {
                    visible: filterVisible,
                    onChange: (param: ColSearchParam[]) => {
                      formatSearchParams(param);
                    },
                  } as any)
                : renderElement,
            },
          };
        } else {
          return null;
        }
      })
      .filter((m) => m);
  }, [props.columns, filterParam]);

  return (
    <div
      style={props.style}
      className={classNames(styles.table, props.className)}
    >
      {hasToolBar && (
        <div className={styles.toolbar}>
          {props.searchConfig?.render?.({ setExtraParam })}
          {props.searchConfig &&
            Object.keys(props.searchConfig).some((m) => m !== "render") && (
              <div className={styles.searchBlock}>
                <GWSearch
                  typeOptions={props.searchConfig?.typeOptions}
                  apiName={props.tableName}
                  placeholder={props.searchConfig?.placeholder}
                  onInputChange={search}
                  extraParam={props.extraParam}
                />
              </div>
            )}
          <div className={styles.operationBar}>
            {Object.getOwnPropertyNames(props.operationBarConfig || {})
              .map((m) => {
                if (optBarPropsMap[m] && props.operationBarConfig[m])
                  return (
                    <Button
                      key={m}
                      // type="primary"
                      loading={optLoading}
                      {...optBarPropsMap[m]}
                    />
                  );
                if (props?.plugins && props.plugins[m]) {
                  const plugin = props.plugins[m];
                  if (!plugin.component) return "";
                  return (
                    <plugin.component
                      key={m}
                      type="operation"
                      refresh={pluginRefreshTable}
                    />
                  );
                }
                return "";
              })
              .filter((m) => m)}
          </div>
        </div>
      )}
      <div className={styles.content}>
        <div className={styles.tableBlock}>
          <Table
            size={props.size}
            columns={columns}
            toolbarState={toolbarState}
            setToolbarState={setToolbarState}
            rowKey={(record) => record.id ?? record.code}
            rowSelection={props.uncheckable ? null : rowSelection}
            dataSource={listData}
            loading={listDataState.loading}
            onChange={handleTableChange}
            pagination={props.notPage ? false : pagination}
            renderSearchPopover={renderSearchPopoverProps}
          />
        </div>
      </div>
      <RelationModal {...relationModalConfig} onOk={handleRelationOk} />
      <LeadingInModal
        {...leadingInConfig}
        onCancel={handleLeadingInCancel}
        onOk={handleLeadingInOk}
      />
      <Confirm
        visible={confirmVisible}
        onClose={() => {
          setConfirmVisible(false);
        }}
        title={
          confirmObj.inBatch
            ? props.tipsConfig?.batchDelete || "确认删除选中内容？"
            : props.tipsConfig?.delete || "确认删除？"
        }
        okText={"确定"}
        cancelText={"取消"}
        onOk={() => {
          deleteItems(confirmObj.deleteIds, confirmObj.record);
          rowLoadingRef.current = false;
        }}
        onCancel={() => {
          if (confirmObj.inBatch) {
            onSelectChange([], []);
          }
          rowLoadingRef.current = false;
        }}
      />
    </div>
  );
};

export default GWTable;

GWTable.styles = styles;
